import { useEffect, useRef, useState } from "react";
import "./CustomCursor.css";

/* CustomCursor — three layers that together replace the OS cursor:

   1. Trail canvas — a full-viewport <canvas> behind everything.
      Each frame we emit particles at the pointer's position,
      modulated by velocity. Particles drift, fade, and die after
      ~0.7-1.0s. Additive blending gives the glow a soft bloom in
      the site's FLC green.
   2. Ring — 36px navy-outline circle with a soft green glow, eased
      lerp toward the pointer (0.18). Blooms on hover, snaps tight
      on click, stretches along the scroll axis while scrolling.
   3. Dot — 12px green core with a 1.5px navy outline (reads on
      both cream and navy backgrounds). Tracks the pointer at
      lerp 0.55 so click targets stay accurate.

   Off on touch (`pointer: coarse`) and reduced-motion: the three
   layers are not rendered at all, so nothing can sit parked at the
   top-left corner of a phone screen. The OS caret is restored
   inside text fields. */

const lerp = (a, b, t) => a + (b - a) * t;
const rand = (a, b) => a + Math.random() * (b - a);

const HOVER_SELECTOR =
  'a, button, [role="button"], input, textarea, select, label, summary, [data-cursor="hover"]';

const MAX_PARTICLES = 240;

/* True only on a device with a precise pointer that also wants
   motion. Read once per mount, then kept in sync with the media
   queries so a plugged-in mouse or an OS motion setting change is
   picked up without a reload. */
const wantsCursor = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return (
    window.matchMedia("(pointer: fine)").matches &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
};

export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const canvasRef = useRef(null);
  /* false on the first render so hydration matches the pre-rendered
     HTML (which never includes the cursor); the effect below turns it
     on right after mount where the device qualifies. */
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const queries = [
      window.matchMedia("(pointer: fine)"),
      window.matchMedia("(prefers-reduced-motion: reduce)"),
    ];
    const sync = () => setEnabled(wantsCursor());
    queries.forEach((q) => q.addEventListener("change", sync));
    sync();
    return () => queries.forEach((q) => q.removeEventListener("change", sync));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!enabled) return undefined;

    document.documentElement.classList.add("has-custom-cursor");

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext ? canvas.getContext("2d") : null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const hasTrail = !!ctx;

    const resize = () => {
      if (!hasTrail) return;
      canvas.width  = Math.floor(window.innerWidth  * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width  = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let prevX = mouseX;
    let prevY = mouseY;
    let dotX = mouseX;
    let dotY = mouseY;
    let ringX = mouseX;
    let ringY = mouseY;
    let scrollVelocity = 0;
    let raf;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    /* Optional scroll-velocity hook (Lenis emits this — we accept
       0 when it's not around, no error). */
    const onScrollEvent = (e) => {
      scrollVelocity = e?.detail?.velocity ?? 0;
    };
    window.addEventListener("lenis:scroll", onScrollEvent);

    const onOver = (e) => {
      const dot = dotRef.current;
      const ring = ringRef.current;
      if (!dot || !ring) return;
      if (e.target?.closest?.(HOVER_SELECTOR)) {
        ring.classList.add("cursor-ring--hover");
        dot.classList.add("cursor-dot--hover");
      } else {
        ring.classList.remove("cursor-ring--hover");
        dot.classList.remove("cursor-dot--hover");
      }
    };
    window.addEventListener("pointerover", onOver, { passive: true });

    const onDown = () => {
      ringRef.current?.classList.add("cursor-ring--down");
      const burst = 14;
      for (let i = 0; i < burst; i++) {
        const ang = (i / burst) * Math.PI * 2;
        spawnParticle(
          mouseX,
          mouseY,
          Math.cos(ang) * rand(0.6, 1.4),
          Math.sin(ang) * rand(0.6, 1.4),
          rand(2, 4),
          rand(0.55, 0.75),
        );
      }
    };
    const onUp = () => {
      ringRef.current?.classList.remove("cursor-ring--down");
    };
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });

    const onLeave = () => {
      dotRef.current?.classList.add("cursor-dot--hidden");
      ringRef.current?.classList.add("cursor-ring--hidden");
    };
    const onEnter = () => {
      dotRef.current?.classList.remove("cursor-dot--hidden");
      ringRef.current?.classList.remove("cursor-ring--hidden");
    };
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    /* ── Particle system ───────────────────────────────────────── */
    const particles = new Array(MAX_PARTICLES);
    let pIdx = 0;
    let pCount = 0;
    for (let i = 0; i < MAX_PARTICLES; i++) particles[i] = null;

    const spawnParticle = (x, y, vx, vy, size, life) => {
      particles[pIdx] = { x, y, vx, vy, size, life, maxLife: life };
      pIdx = (pIdx + 1) % MAX_PARTICLES;
      if (pCount < MAX_PARTICLES) pCount++;
    };

    const tick = () => {
      dotX = lerp(dotX, mouseX, 0.55);
      dotY = lerp(dotY, mouseY, 0.55);
      ringX = lerp(ringX, mouseX, 0.18);
      ringY = lerp(ringY, mouseY, 0.18);

      const v = Math.max(-40, Math.min(40, scrollVelocity));
      const stretchY = 1 + Math.abs(v) * 0.012;
      const stretchX = 1 / Math.sqrt(stretchY);

      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${dotX - 6}px, ${dotY - 6}px, 0)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform =
          `translate3d(${ringX - 18}px, ${ringY - 18}px, 0) ` +
          `scale(${stretchX}, ${stretchY})`;
        if (Math.abs(v) > 4) {
          ringRef.current.classList.add("cursor-ring--scrolling");
        } else {
          ringRef.current.classList.remove("cursor-ring--scrolling");
        }
      }

      const dx = mouseX - prevX;
      const dy = mouseY - prevY;
      const dist = Math.hypot(dx, dy);
      if (dist > 0.5) {
        const steps = Math.min(6, Math.max(1, Math.floor(dist / 6)));
        for (let i = 0; i < steps; i++) {
          const t = (i + 1) / steps;
          const px = prevX + dx * t;
          const py = prevY + dy * t;
          spawnParticle(
            px + rand(-1.2, 1.2),
            py + rand(-1.2, 1.2),
            rand(-0.25, 0.25),
            rand(-0.25, 0.25) + 0.05,
            rand(3, 6) * Math.min(1, 0.6 + dist / 60),
            rand(0.55, 0.85),
          );
        }
      }
      prevX = mouseX;
      prevY = mouseY;

      if (hasTrail) {
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
        ctx.globalCompositeOperation = "lighter";

        for (let i = 0; i < MAX_PARTICLES; i++) {
          const p = particles[i];
          if (!p) continue;
          p.life -= 1 / 60;
          if (p.life <= 0) { particles[i] = null; continue; }
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.94;
          p.vy *= 0.94;
          const t = p.life / p.maxLife;
          const r = p.size * (0.45 + 0.55 * t);
          const a = t * 0.75;
          /* FLC green bloom: bright green core, mid green,
             fade to transparent. */
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 3);
          grad.addColorStop(0,   `rgba(180, 230, 130, ${a})`);
          grad.addColorStop(0.4, `rgba(111, 184, 74,  ${a * 0.55})`);
          grad.addColorStop(1,   `rgba(79,  154, 46,  0)`);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r * 3, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalCompositeOperation = "source-over";
      }

      scrollVelocity = lerp(scrollVelocity, 0, 0.08);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerover", onOver);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("lenis:scroll", onScrollEvent);
      window.removeEventListener("resize", resize);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <canvas ref={canvasRef} className="cursor-trail" aria-hidden="true" data-prerender="omit" />
      <div ref={ringRef} className="cursor-ring" aria-hidden="true" data-prerender="omit" />
      <div ref={dotRef}  className="cursor-dot"  aria-hidden="true" data-prerender="omit" />
    </>
  );
}
